import { useState, useRef, memo, useCallback, useEffect } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, CheckCircle, Loader2, ArrowRight, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { trackPhoneCallRequest, trackPhoneCallSuccess } from "@/lib/analytics";
import { submitLead } from "@/hooks/useLeadTracking";
import GoogleReviewsBadge from "./GoogleReviewsBadge";
import HeroUtilitySelector from "./HeroUtilitySelector";
import ZipScarcityLine from "./ZipScarcityLine";
import SmsConsentCheckbox from "./SmsConsentCheckbox";
import { useUtilityPreference, getRateHikeSubhead } from "@/hooks/useUtilityPreference";
// URL imports prevent both images from being bundled - picture element fetches only the matching one
import hero1 from "@/assets/hero-variant-1.jpg?url";
import hero2 from "@/assets/hero-variant-2.jpg?url";
import hero3 from "@/assets/hero-variant-3.jpg?url";
import hero4 from "@/assets/hero-variant-4.jpg?url";
import hero1Mobile from "@/assets/hero-variant-1-mobile.jpg?url";
import hero2Mobile from "@/assets/hero-variant-2-mobile.jpg?url";
import hero3Mobile from "@/assets/hero-variant-3-mobile.jpg?url";
import hero4Mobile from "@/assets/hero-variant-4-mobile.jpg?url";

// Pick a hero variant once per browser session (resets when tab closes / new session)
const HERO_DESKTOP = [hero1, hero2, hero3, hero4];
const HERO_MOBILE = [hero1Mobile, hero2Mobile, hero3Mobile, hero4Mobile];
const getHeroVariantIndex = (): number => {
  if (typeof window === "undefined") return 0;
  try {
    const stored = sessionStorage.getItem("heroVariantIndex");
    if (stored !== null) {
      const n = parseInt(stored, 10);
      if (!isNaN(n) && n >= 0 && n < 4) return n;
    }
    const idx = Math.floor(Math.random() * 4);
    sessionStorage.setItem("heroVariantIndex", String(idx));
    return idx;
  } catch {
    return Math.floor(Math.random() * 4);
  }
};
const heroVariantIndex = getHeroVariantIndex();
const heroImage = HERO_DESKTOP[heroVariantIndex];
const heroImageMobile = HERO_MOBILE[heroVariantIndex];


// Memoized form component to prevent re-renders
const HeroPhoneForm = memo(() => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const { toast } = useToast();

  const formatPhoneNumber = useCallback((value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  }, []);

  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(formatPhoneNumber(e.target.value));
  }, [formatPhoneNumber]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const cleanedNumber = phoneNumber.replace(/\D/g, "");
    if (cleanedNumber.length < 10) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid 10-digit phone number.",
        variant: "destructive",
      });
      return;
    }
    
    // Block obviously fake numbers client-side
    if (cleanedNumber.startsWith('555') && cleanedNumber.slice(3, 6) === '555') {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a real phone number, not a test number.",
        variant: "destructive",
      });
      return;
    }
    
    // Area code must start with 2-9
    if (!/^[2-9]/.test(cleanedNumber)) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid US phone number with a real area code.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const formattedNumber = `+1${cleanedNumber}`;
      
      await submitLead({
        phone_number: formattedNumber,
        first_name: firstName || undefined,
        email: email || undefined,
        sms_opt_in: smsOptIn,
        form_source: 'hero_form',
      });
      
      const { data, error } = await supabase.functions.invoke("retell-call", {
        body: { phoneNumber: formattedNumber, firstName: firstName || "Friend", email: email || undefined },
      });

      if (error) {
        throw new Error(error.message || "Call failed");
      }
      
      // Check for error in response body (edge function returns JSON with error field)
      if (data?.error) {
        toast({
          title: "Invalid Phone Number",
          description: data.error,
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      trackPhoneCallRequest({ source: "hero_form", first_name: firstName || undefined });
      trackPhoneCallSuccess({ source: "hero_form" });

      setIsSuccess(true);
      toast({
        title: "Call Incoming!",
        description: "You'll receive a call from our Energy Advisor in seconds.",
      });
      
      setTimeout(() => {
        setIsSuccess(false);
        setPhoneNumber("");
        setFirstName("");
        setEmail("");
      }, 5000);
    } catch (error) {
      console.error("Error triggering call:", error);
      const errorMessage = error instanceof Error ? error.message : "Please try again or call us directly.";
      toast({
        title: "Something went wrong",
        description: errorMessage.includes("phone") ? errorMessage : "Please try again or call us directly.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="text-center py-4 animate-fade-in">
        <div className="w-14 h-14 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-3">
          <Phone className="h-7 w-7 text-secondary animate-pulse" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-1">Call Coming!</h3>
        <p className="text-sm text-muted-foreground">
          Our Energy Advisor is calling you now...
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            type="text"
            placeholder="Your Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="flex-1 bg-background border-border"
          />
          <Input
            type="tel"
            placeholder="(555) 555-5555"
            value={phoneNumber}
            onChange={handlePhoneChange}
            required
            className="flex-1 bg-background border-border"
          />
        </div>
        <Input
          type="email"
          placeholder="Email (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-background border-border"
        />
      </div>
      <SmsConsentCheckbox id="hero-sms-opt-in" checked={smsOptIn} onChange={setSmsOptIn} />
      <Button
        type="submit"
        disabled={isLoading}
        className="w-full bg-gradient-cta hover:opacity-90 text-accent-foreground font-semibold text-lg py-6 shadow-lg animate-pulse-glow"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Connecting...
          </>
        ) : (
          <>
            <Phone className="w-5 h-5 mr-2" />
            See How Much I Can Save
          </>
        )}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        By submitting, you agree to be contacted by phone. Msg & data rates may apply for texts. Reply STOP to opt out.
      </p>
    </form>
  );
});
HeroPhoneForm.displayName = "HeroPhoneForm";

const benefits = [
  "Lock In Your Rate",
  "25 Year Protection",
  "Battery Backup Included",
];

const Hero = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [scrollReady, setScrollReady] = useState(false);

  // Defer scroll tracking slightly to not block initial paint
  useEffect(() => {
    const timer = setTimeout(() => setScrollReady(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // useScroll activates after brief delay to prioritize content paint
  const { scrollYProgress } = useScroll({
    target: scrollReady ? sectionRef : undefined,
    offset: ["start start", "end start"],
  });

  // Parallax transforms - background moves slower than scroll
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const backgroundScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const backgroundOpacity = useTransform(scrollYProgress, [0, 0.8], [0.25, 0.08]);

  // No skeleton blocking - content renders immediately for fastest LCP
  return (
    <section 
      ref={sectionRef}
      id="home" 
      className="relative min-h-[85vh] md:min-h-[75vh] flex items-center pt-36 md:pt-44 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-sky" />
      
      {/* Hero Image with Parallax Effect - Uses <picture> to load ONLY the correct image */}
      <motion.div 
        className="absolute inset-0"
        style={{ y: backgroundY }}
      >
        <motion.div
          style={{ 
            scale: backgroundScale,
            opacity: backgroundOpacity,
          }}
          className="w-full h-[120%]"
        >
          {/* Picture element ensures only ONE image downloads based on media query */}
          <picture>
            <source 
              media="(max-width: 767px)" 
              srcSet={heroImageMobile}
              type="image/jpeg"
            />
            <source 
              media="(min-width: 768px)" 
              srcSet={heroImage}
              type="image/jpeg"
            />
            <img
              src={heroImageMobile}
              alt="Professional solar panel installation on a modern California home with rooftop solar array"
              width={768}
              height={1024}
              className="w-full h-full object-cover"
              fetchPriority="high"
              decoding="sync"
            />
          </picture>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />
      </motion.div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Content - Editorial composition */}
          <HeroContent />

          {/* Hero Image Card - Desktop only, simplified */}
          <div className="hidden lg:block animate-fade-in">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl border border-border">
                <img
                  src={heroImage}
                  alt="California residential home with solar panels installed on rooftop - Save up to 60% on energy bills"
                  className="w-full h-auto"
                  width={600}
                  height={400}
                  loading="lazy"
                  decoding="async"
                />
              </div>

              {/* Floating Stats Card */}
              <div className="absolute -top-6 -right-6 bg-card rounded-xl p-4 shadow-xl border border-border">
                <div className="text-3xl font-bold text-secondary">60%</div>
                <div className="text-base text-muted-foreground">Average Savings</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/** Hero content split out so it can subscribe to utility preference. */
const HeroContent = () => {
  const { utility } = useUtilityPreference();
  const subhead = getRateHikeSubhead(utility);
  const [showCallback, setShowCallback] = useState(false);

  const scrollToEstimate = useCallback(() => {
    const el = document.getElementById("energy-savings-calculator");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div className="text-center lg:text-left animate-fade-in">
      {/* Google Reviews Badge */}
      <GoogleReviewsBadge rating={5.0} reviewCount={47} className="mb-6" />

      {/* Eyebrow */}
      <div className="flex items-center gap-3 mb-5 justify-center lg:justify-start">
        <div className="h-px w-10 bg-secondary" />
        <span className="eyebrow text-secondary">
          {utility ? `${utility} customers — $0-Down Solar PPA` : "$0-Down Solar PPA for California"}
        </span>
      </div>

      <h1 className="hero-headline font-display text-4xl md:text-5xl lg:text-6xl text-foreground leading-[1.05] mb-5 md:mb-7">
        Cut Your California Power Bill by{" "}
        <em className="not-italic text-secondary">Up to 60%</em>
        <br />
        Without Buying Solar.
      </h1>

      <p className="hero-subheadline text-base md:text-lg lg:text-xl text-muted-foreground mb-6 md:mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed">
        $0 down. Locked monthly rate for 25 years. Battery backup included.{" "}
        <span className="text-foreground/80">{subhead}</span>
      </p>

      {/* Utility selector — first interaction, localizes the page */}
      <div className="max-w-md mx-auto lg:mx-0 mb-5">
        <HeroUtilitySelector />
      </div>

      {/* Benefits List */}
      <div className="flex flex-wrap justify-center lg:justify-start gap-3 md:gap-4 mb-6 md:mb-8">
        {benefits.map((benefit) => (
          <div key={benefit} className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-secondary" />
            <span className="text-base font-medium text-foreground">{benefit}</span>
          </div>
        ))}
      </div>

      {/* Dual CTA */}
      <div className="max-w-md mx-auto lg:mx-0 space-y-3">
        <Button
          type="button"
          onClick={scrollToEstimate}
          className="w-full bg-gradient-cta hover:opacity-90 text-accent-foreground font-semibold text-base md:text-lg py-6 shadow-xl animate-pulse-glow touch-manipulation"
        >
          Get My Free Savings Estimate
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>

        <button
          type="button"
          onClick={() => setShowCallback((v) => !v)}
          className="w-full flex items-center justify-center gap-2 text-sm md:text-base text-foreground/80 hover:text-foreground py-3 font-medium touch-manipulation"
          aria-expanded={showCallback}
        >
          <Phone className="w-4 h-4" />
          Or talk to an Energy Advisor in 30 seconds
          <ChevronDown
            className={`w-4 h-4 transition-transform ${showCallback ? "rotate-180" : ""}`}
          />
        </button>

        <AnimatePresence initial={false}>
          {showCallback && (
            <motion.div
              key="callback"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="bg-card/80 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-xl border border-border">
                <div className="text-center lg:text-left mb-4">
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    Request a callback
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Real Californian on the line — no high-pressure pitch.
                  </p>
                </div>
                <HeroPhoneForm />
                <ZipScarcityLine className="mt-4" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default memo(Hero);


